import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { startCartCheckout, validateDiscountCode, genIdempotencyKey } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const CART_KEY = "pw_cart_v1";
const CartCtx = createContext(null);

function readCart() {
  try {
    const raw = window.sessionStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readCart);
  const [open, setOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [discountCode, setDiscountCode] = useState(null);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {
      /* storage unavailable, cart stays in-memory for this render */
    }
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = `${item.lookupKey}::${item.variantNote || ""}`;
      const existing = prev.find((p) => `${p.lookupKey}::${p.variantNote || ""}` === key);
      if (existing) {
        return prev.map((p) => (p.id === existing.id ? { ...p, quantity: p.quantity + item.quantity } : p));
      }
      return [...prev, { ...item, id: `${key}::${Date.now()}` }];
    });
    trackEvent("add_to_cart", {
      product_name: item.name,
      product_category: item.category,
      product_price: item.unitPrice,
      currency: "USD",
      value: item.unitPrice * item.quantity,
      quantity: item.quantity,
      selected_color: item.color,
      selected_size: item.size,
    });
    setOpen(true);
    trackEvent("shop_cart_opened", { product_name: item.name, cart_value: item.unitPrice * item.quantity });
    toast.success(`${item.name} added to cart`);
  }, []);

  const removeItem = useCallback((id) => {
    idempotencyKeyRef.current = null;
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    idempotencyKeyRef.current = null;
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, quantity: Math.max(1, quantity) } : p)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setDiscountCode(null);
    setDiscountInfo(null);
    setCheckoutError(null);
    idempotencyKeyRef.current = null;
  }, []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.unitPrice * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const discountAmount = discountInfo ? discountInfo.discount_amount / 100 : 0;
  const total = Math.max(0, subtotal - discountAmount);

  const removeDiscount = useCallback(() => {
    setDiscountCode(null);
    setDiscountInfo(null);
  }, []);

  const applyDiscount = useCallback(async (codeInput) => {
    const code = (codeInput || "").trim().toUpperCase();
    if (!code) return { ok: false };
    setDiscountLoading(true);
    try {
      const data = await validateDiscountCode(code, items);
      setDiscountCode(data.code);
      setDiscountInfo(data);
      trackEvent("discount_code_applied", { code: data.code, discount_amount: data.discount_amount / 100 });
      toast.success(`${data.code} applied`);
      return { ok: true };
    } catch (err) {
      const message = err?.response?.data?.detail || "Invalid or expired code";
      toast.error(message);
      return { ok: false, message };
    } finally {
      setDiscountLoading(false);
    }
  }, [items]);

  // Re-validate a previously applied code whenever the cart contents change
  // (e.g. removing the Complete System should drop an eligible-only discount).
  useEffect(() => {
    if (!discountCode) return;
    validateDiscountCode(discountCode, items)
      .then(setDiscountInfo)
      .catch(() => {
        setDiscountCode(null);
        setDiscountInfo(null);
        toast.error("Discount removed — your cart no longer qualifies for that code.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const checkout = useCallback(async () => {
    if (!items.length || checkingOut) return;
    setCheckingOut(true);
    setCheckoutError(null);
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = genIdempotencyKey();
    trackEvent("checkout_started", { value: subtotal, currency: "USD", product_category: "cart" });
    trackEvent("shop_checkout_started", { cart_value: subtotal, quantity: items.length });
    try {
      await startCartCheckout(
        items.map((i) => ({ lookup_key: i.lookupKey, quantity: i.quantity, variant_note: i.variantNote })),
        discountCode,
        idempotencyKeyRef.current
      );
    } catch (err) {
      const message = err?.code === "ECONNABORTED"
        ? "Checkout is taking longer than expected. Please try again."
        : (err?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setCheckoutError(message);
      toast.error(message);
      setCheckingOut(false);
    }
  }, [items, subtotal, checkingOut, discountCode]);

  const value = {
    items, addItem, removeItem, updateQuantity, clearCart, subtotal, count, open, setOpen, checkout, checkingOut,
    checkoutError, discountCode, discountInfo, discountLoading, discountAmount, total, applyDiscount, removeDiscount,
  };
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
